import React, { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX, ShieldAlert, Check, RefreshCw, Eye, EyeOff } from 'lucide-react';
import createSocket from '../services/socketapi';

interface IntrusionEvent {
    event_id: number;
    event_type: string;
    event_status: string;
    severity: string;
    message: string;
    event_data?: {
        intrusion_type?: string;
        camera_name?: string;
        ip_address?: string;
        snapshot_url?: string;
        confidence?: number;
    };
    created_at: string;
}

export const IntrusionAlarm: React.FC = () => {
    const [events, setEvents] = useState<IntrusionEvent[]>([]);
    const [isMuted, setIsMuted] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [isAlarmActive, setIsAlarmActive] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const socketRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<any>(null);
    const timeoutsRef = useRef<{ [key: number]: any }>({});

    // Initialize & manage audio instance
    useEffect(() => {
        return () => {
            // Clean up all active timeouts
            if (timeoutsRef.current) {
                Object.values(timeoutsRef.current).forEach(clearTimeout);
            }
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    // Play or stop alarm sound based on isAlarmActive & mute state
    useEffect(() => {
        if (isAlarmActive && !isMuted && events.length > 0) {
            if (!audioRef.current) {
                const audio = new Audio('/alarm.mp3');
                audio.loop = true;
                audioRef.current = audio;
                audio.play().catch(err => console.warn('Audio auto-play blocked by browser permissions:', err));
            }
        } else {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        }
    }, [isAlarmActive, isMuted, events.length]);

    // Connect to WebSocket
    const connectWS = () => {
        if (socketRef.current) {
            socketRef.current.close();
        }

        try {
            const socket = createSocket('intrusion-events');
            socketRef.current = socket;

            socket.onopen = () => {
                console.log('[WebSocket] Connected to intrusion-events');
                setIsConnected(true);
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                    reconnectTimeoutRef.current = null;
                }
            };

            socket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'initial_events') {
                        // Ignore initial preloaded events completely
                    } else if (data.type === 'create') {
                        if (data.event_type === 'intrusion_detection') {
                            setEvents(prev => {
                                // Prevent duplicate IDs
                                if (prev.some(e => e.event_id === data.event_id)) return prev;
                                return [...prev, data];
                            });
                            setIsAlarmActive(true); // Play sound for new live alerts
                            setIsExpanded(true); // Automatically show pop up card for live alerts

                            // Automatically clear the alert after 30 seconds
                            const timerId = setTimeout(() => {
                                handleAcknowledge(data.event_id);
                            }, 30000);
                            timeoutsRef.current[data.event_id] = timerId;
                        }
                    } else if (
                        data.type === 'update' ||
                        data.type === 'delete' ||
                        data.event_status === 'resolved' ||
                        data.event_status === 'closed'
                    ) {
                        // Remove the alert if it is resolved or updated
                        setEvents(prev => {
                            const remaining = prev.filter(e => e.event_id !== data.event_id);
                            if (remaining.length === 0) {
                                setIsAlarmActive(false);
                            }
                            return remaining;
                        });
                    }
                } catch (err) {
                    console.error('[WebSocket] Error parsing intrusion alert message', err);
                }
            };

            socket.onclose = () => {
                console.log('[WebSocket] Connection closed. Retrying in 5s...');
                setIsConnected(false);
                scheduleReconnect();
            };

            socket.onerror = (err) => {
                console.error('[WebSocket] Error in intrusion socket:', err);
                socket.close();
            };
        } catch (err) {
            console.error('[WebSocket] Failed to connect:', err);
            scheduleReconnect();
        }
    };

    const scheduleReconnect = () => {
        if (reconnectTimeoutRef.current) return;
        reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            connectWS();
        }, 5000);
    };

    useEffect(() => {
        connectWS();
        return () => {
            if (socketRef.current) {
                socketRef.current.close();
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
        };
    }, []);

    // Dismiss a specific event locally
    const handleAcknowledge = (eventId: number) => {
        if (timeoutsRef.current[eventId]) {
            clearTimeout(timeoutsRef.current[eventId]);
            delete timeoutsRef.current[eventId];
        }
        setEvents(prev => {
            const remaining = prev.filter(e => e.event_id !== eventId);
            if (remaining.length === 0) {
                setIsAlarmActive(false);
            }
            return remaining;
        });
    };

    if (events.length === 0) return null;

    return (
        <div className="fixed bottom-5 left-6 z-[9999] w-[280px] flex flex-col gap-2 pointer-events-none">
            {/* Global Controls Bar */}
            <div className="bg-slate-900/90 backdrop-blur-md border border-rose-500/20 text-white rounded-lg px-3 py-1.5 flex items-center justify-between pointer-events-auto animate-in slide-in-from-bottom duration-300">
                <div className="flex items-center gap-1.5">
                    <span className="relative flex h-1.5 w-1.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-rose-500"></span>
                    </span>
                    <span className="text-[9px] font-black uppercase tracking-wider text-rose-400">
                        Intrusion Alert ({events.length})
                    </span>
                </div>
                <div className="flex items-center gap-1.5">
                    <button
                        onClick={() => setIsExpanded(prev => !prev)}
                        className={`p-1 rounded-md border transition-all ${
                            isExpanded
                                ? 'bg-indigo-950/40 border-indigo-900 text-indigo-400 hover:bg-indigo-900/50'
                                : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-750'
                        }`}
                        title={isExpanded ? 'Hide Alerts' : 'Show Alerts'}
                    >
                        {isExpanded ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    </button>
                    <button
                        onClick={() => setIsMuted(prev => !prev)}
                        className={`p-1 rounded-md border transition-all ${
                            isMuted 
                                ? 'bg-rose-950/40 border-rose-900 text-rose-400 hover:bg-rose-900/50' 
                                : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-750'
                        }`}
                        title={isMuted ? 'Unmute Alarm' : 'Mute Alarm'}
                    >
                        {isMuted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                    </button>
                    {!isConnected && (
                        <span className="p-1 rounded-md bg-slate-800 border border-slate-700 text-amber-400" title="Reconnecting WebSocket...">
                            <RefreshCw className="w-3 h-3 animate-spin" />
                        </span>
                    )}
                </div>
            </div>

            {/* Event Cards Stack (Shows top 3 newest events) */}
            {isExpanded && (
                <div className="flex flex-col-reverse gap-2">
                    {events.slice(-3).map((item) => {
                        const confidence = item.event_data?.confidence;
                        const confidencePercent = confidence ? Math.round(confidence * 100) : null;
                        const isHighConfidence = confidence ? confidence >= 0.8 : false;

                        return (
                            <div
                                key={item.event_id}
                                className="bg-slate-950/95 backdrop-blur-md border border-rose-500 rounded-xl overflow-hidden flex flex-col pointer-events-auto animate-in slide-in-from-left duration-300 border-pulse"
                            >
                                {/* Card Header */}
                                <div className="bg-rose-950/30 px-2.5 py-1.5 border-b border-rose-900/30 flex items-center justify-between">
                                    <div className="flex items-center gap-1.5">
                                        <div className="p-0.5 bg-rose-500/20 rounded">
                                            <ShieldAlert className="w-3.5 h-3.5 text-rose-500 animate-pulse" />
                                        </div>
                                        <div>
                                            <h4 className="text-[10px] font-black text-rose-400 uppercase tracking-wider leading-none">
                                                {item.event_data?.camera_name || 'Surveillance Camera'}
                                            </h4>
                                            {item.event_data?.ip_address && (
                                                <span className="text-[8px] font-bold text-slate-500 tracking-wide block mt-0.5">
                                                    IP: {item.event_data.ip_address}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleAcknowledge(item.event_id)}
                                        className="p-0.5 bg-slate-900 hover:bg-slate-800 border border-slate-800 hover:border-slate-700 text-rose-400 hover:text-rose-300 rounded transition-all animate-none"
                                        title="Acknowledge"
                                    >
                                        <Check className="w-3 h-3" />
                                    </button>
                                </div>

                                {/* Alert Content */}
                                <div className="p-2.5 space-y-1.5">
                                    <div className="text-[10px] font-bold text-slate-200">
                                        {item.message || 'Intrusion Activity Detected'}
                                    </div>

                                    {item.event_data?.snapshot_url && (
                                        <div className="relative aspect-video bg-black rounded-lg overflow-hidden border border-slate-800 group shadow-inner">
                                            <img
                                                src={item.event_data.snapshot_url}
                                                alt="Intrusion Snapshot"
                                                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-1.5 pointer-events-none">
                                                {confidencePercent !== null && (
                                                    <span className={`text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full border shadow-md ${
                                                        isHighConfidence 
                                                            ? 'bg-emerald-950/80 border-emerald-500/30 text-emerald-400' 
                                                            : 'bg-amber-950/80 border-amber-500/30 text-amber-400'
                                                    }`}>
                                                        Conf: {confidencePercent}%
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between text-[8px] text-slate-400 font-bold uppercase tracking-wider">
                                        <span className="truncate max-w-[65%]">Type: {item.event_data?.intrusion_type || 'Zone Intrusion'}</span>
                                        <span>{new Date(item.created_at).toLocaleTimeString()}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Custom Pulse Styling */}
            <style>{`
                @keyframes borderPulse {
                    0% { border-color: rgb(244, 63, 94); }
                    50% { border-color: rgba(244, 63, 94, 0.4); }
                    100% { border-color: rgb(244, 63, 94); }
                }
                .border-pulse {
                    animation: borderPulse 2s infinite ease-in-out;
                }
            `}</style>
        </div>
    );
};

export default IntrusionAlarm;
